import * as tools from "./tools.js";
import {
  PerspectiveCamera,
  Scene,
  Texture,
  LinearFilter,
  MeshBasicMaterial,
  PlaneGeometry,
  Mesh,
  WebGLRenderer,
} from "three";
import RenderPass from "./bad-tv/postprocessing/RenderPass.js";
import ShaderPass from "./bad-tv/postprocessing/ShaderPass.js";
import EffectComposer from "./bad-tv/postprocessing/EffectComposer.js";
import FilmShader from "./bad-tv/shaders/FilmShader.js";
import StaticShader from "./bad-tv/shaders/StaticShader.js";
import BadTVShader from "./bad-tv/shaders/BadTVShader.js";
import RGBShiftShader from "./bad-tv/shaders/RGBShiftShader.js";
import CopyShader from "./bad-tv/shaders/CopyShader.js";
const THREE = {
  PerspectiveCamera,
  Scene,
  Texture,
  LinearFilter,
  MeshBasicMaterial,
  PlaneGeometry,
  Mesh,
  WebGLRenderer,
};

class OSD {
  constructor(tv, font) {
    this.font = font;
    this.tv = tv;
    this.timeSinceVolume = 0;
    this.timeSinceChannel = 0;
  }
  draw(ctx) {
    ctx.save();
    if (Date.now() < this.timeSinceChannel + 6000) {
      this.font.draw(
        ctx,
        this.tv.selectedChannelName,
        16,
        16,
        16,
        16,
        16,
        "#00FF00",
      );
    }
    if (this.timeSinceVolume + 3000 > Date.now()) {
      const volumeStr =
        "|".repeat(this.tv.volume) +
        "·".repeat(this.tv.maxVolume - this.tv.volume);
      this.font.draw(
        ctx,
        "VOLUME\n" + volumeStr,
        16,
        10 * 16,
        16,
        16,
        16,
        "#00FF00",
      );
    }
    ctx.restore();
  }
}

export class VideoCable {
  constructor(name) {
    this.name = name;
    this.features = {};
    this._source = undefined;
  }
  get source() {
    return this._source;
  }
  set source(canvas) {
    this._source = canvas
      ? { canvas: canvas, ctx: canvas.getContext("2d") }
      : undefined;
  }
}

export default class TV {
  constructor(OSDFont, volume = 5, maxVolume = 18) {
    const stored = parseInt(window.localStorage.getItem("tvVolume"));
    this._volume = isNaN(stored) ? volume : stored;
    this.maxVolume = maxVolume;
    this.videoCables = {};
    this._selectedChannelName = undefined;
    this._videoCable = undefined;
    this._sourceCanvas = undefined;
    this.OSD = new OSD(this, OSDFont);

    const tvXres = 1080;
    const tvYres = 720;

    // THREE is imported at module level

    const camera = new THREE.PerspectiveCamera(55, tvXres / tvYres, 20, 3000);
    camera.position.z = 1000;
    const scene = new THREE.Scene();

    this.videoCanvas = document.createElement("canvas");
    this.videoCanvas.id = "VIDEO_CANVAS";
    this.videoCanvas.width = 320;
    this.videoCanvas.height = 200;

    this.videoTexture = new THREE.Texture(this.videoCanvas);
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;
    const videoMaterial = new THREE.MeshBasicMaterial({
      map: this.videoTexture,
    });

    const planeGeometry = new THREE.PlaneGeometry(tvXres, tvYres, 1, 1);
    const plane = new THREE.Mesh(planeGeometry, videoMaterial);
    scene.add(plane);
    plane.z = 0;
    plane.scale.x = plane.scale.y = 1.45;

    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(640, 480);

    const renderPass = new RenderPass(scene, camera);
    this.badTVPass = new ShaderPass(BadTVShader);
    const rgbPass = new ShaderPass(RGBShiftShader);
    this.filmPass = new ShaderPass(FilmShader);
    this.staticPass = new ShaderPass(StaticShader);
    const copyPass = new ShaderPass(CopyShader);

    this.filmPass.uniforms.grayscale.value = 0;
    this.staticPass.uniforms["amount"].value = 0.5;
    this.staticPass.uniforms["size"].value = 2;

    this.fixTv = () => {
      this.staticPass.uniforms["amount"].value = 0;
      this.staticPass.uniforms["size"].value = 1;
      this.badTVPass.uniforms["distortion"].value = 0.1;
      this.badTVPass.uniforms["distortion2"].value = 0.1;
      this.badTVPass.uniforms["speed"].value = 0;
      this.badTVPass.uniforms["rollSpeed"].value = 0;
      rgbPass.uniforms["angle"].value = 0;
      rgbPass.uniforms["amount"].value = 0;
      this.filmPass.uniforms["sCount"].value = 800;
      this.filmPass.uniforms["sIntensity"].value = 0.9;
      this.filmPass.uniforms["nIntensity"].value = 0.4;
    };

    this.breakTv = (st) => {
      this.filmPass.uniforms["sCount"].value = 800;
      this.filmPass.uniforms["sIntensity"].value = 0.9;
      this.filmPass.uniforms["nIntensity"].value = 0.4;
      this.badTVPass.uniforms["distortion"].value = Math.random() * 3 + 0.1;
      this.badTVPass.uniforms["distortion2"].value = Math.random() * 3 + 0.1;
      if (Math.random() > 0.8) {
        this.badTVPass.uniforms["speed"].value = 0.04 + Math.random() * 0.4;
        this.badTVPass.uniforms["rollSpeed"].value = 0.04 + Math.random() * 0.1;
      }
      rgbPass.uniforms["angle"].value = Math.random() * 2;
      rgbPass.uniforms["amount"].value = Math.random() * 0.03;
      st = st || 0.1;
      this.staticPass.uniforms["size"].value = 2 + Math.random() * st * 3;
      this.staticPass.uniforms["amount"].value = st + Math.random() * st;
    };

    this.composer = new EffectComposer(renderer);
    this.composer.addPass(renderPass);
    this.composer.addPass(this.filmPass);
    this.composer.addPass(this.badTVPass);
    this.composer.addPass(rgbPass);
    this.composer.addPass(this.staticPass);
    this.composer.addPass(copyPass);
    copyPass.renderToScreen = true;

    const onResize = () => {
      const paddingForGlitch = 100;
      const tvAspect = (tvXres + paddingForGlitch) / tvYres;
      let width = window.innerWidth;
      let height = window.innerHeight;
      const windowAspect = width / height;
      if (windowAspect < tvAspect) {
        height = width / tvAspect;
      } else {
        width = height * tvAspect;
      }
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", onResize, false);
    onResize();

    this.buzz = (st) =>
      Promise.resolve(this.breakTv(st))
        .then(tools.sleep(Math.random() * 700))
        .then(this.fixTv)
        .then(tools.sleep(Math.random() * 10000));

    this.tvOutput = renderer.domElement;

    this._noSignalCanvas = undefined;
  }

  get volume() {
    return this._volume;
  }
  set volume(v) {
    this._volume = v;
    this.OSD.timeSinceVolume = Date.now();
    Howler.volume(this._volume / this.maxVolume);
    window.localStorage.setItem("tvVolume", this._volume);
  }

  get selectedChannelName() {
    return this._selectedChannelName;
  }
  set selectedChannelName(name) {
    this._selectedChannelName = name;
    this.OSD.timeSinceChannel = Date.now();

    const cable = this._getCable(name);
    if (cable == undefined) {
      this.breakTv(0.3);
    } else {
      this.buzz();
    }
    this._updateSource(cable);
  }

  _getCable(name) {
    return this.videoCables[name];
  }

  _updateSource(cable) {
    if (cable && cable.source) {
      this._sourceCanvas = cable.source.canvas;
      this.videoCanvas.width = this._sourceCanvas.width;
      this.videoCanvas.height = this._sourceCanvas.height;
    } else {
      if (!this._noSignalCanvas) {
        const c = document.createElement("canvas");
        c.width = 320;
        c.height = 200;
        c.id = "NO_SIGNAL_CANVAS";
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, c.width, c.height);
        this._noSignalCanvas = c;
      }
      this._sourceCanvas = this._noSignalCanvas;
    }
  }

  getCable() {
    return this.videoCables[this.selectedChannelName];
  }

  get source() {
    const cable = this.getCable();
    if (cable && cable.source) return cable.source;
    return this._noSignalCanvas
      ? {
          canvas: this._noSignalCanvas,
          ctx: this._noSignalCanvas.getContext("2d"),
        }
      : undefined;
  }

  _setupFeatures(cable) {
    cable.features.volumeUp = this.volumeUp.bind(this);
    cable.features.volumeDown = this.volumeDown.bind(this);
    cable.features.buzz = this.buzz.bind(this);
  }

  insertVideoCable(videoCable) {
    this._setupFeatures(videoCable);
    this.videoCables[videoCable.name] = videoCable;
    if (videoCable.name === this._selectedChannelName) {
      this.buzz();
      this._updateSource(videoCable);
    }
  }

  setChannel(name) {
    this.selectedChannelName = name;
  }

  async start() {
    console.info("TURNING ON TV");
    await this.OSD.font.load();
    Howler.mute(false);
    Howler.volume(this.volume / this.maxVolume);
    this.selectedChannelName = "VIDEO 1";
    return this.tvOutput;
  }

  update(delta) {
    const shaderTimeDelta = delta * 0.006;
    this.badTVPass.uniforms["time"].value += shaderTimeDelta;
    this.filmPass.uniforms["time"].value += shaderTimeDelta;
    this.staticPass.uniforms["time"].value += shaderTimeDelta;
  }

  draw() {
    const videoCtx = this.videoCanvas.getContext("2d");
    videoCtx.clearRect(0, 0, this.videoCanvas.width, this.videoCanvas.height);
    videoCtx.drawImage(this.source.canvas, 0, 0);
    this.OSD.draw(videoCtx);
    this.videoTexture.needsUpdate = true;
    this.composer.render();
  }

  setVolume(vol) {
    if (vol > this.maxVolume || vol < 0) return false;
    this.volume = vol;
    return true;
  }

  volumeUp() {
    return this.setVolume(this.volume + 1);
  }
  volumeDown() {
    return this.setVolume(this.volume - 1);
  }
}
